import React, { useEffect, useRef, useState } from "react";
import {
	Typography,
	Spin,
	Button,
	notification,
	Result,
	Modal,
	Checkbox,
} from "antd";
import { useHistory } from "react-router-dom";
import planoContratualService from "services/PlanoContratualService";
import usuarioService from "services/UsuarioService";
import CheckoutPagamento from "components/organism/CheckoutPagamento";
import { PUBLIC_PREFIX_PATH } from "configs/AppConfig";
import useIsAppWebView from "hooks/useIsAppWebView";

const { Title, Text } = Typography;

const reais = (centavos) => Number(centavos || 0) / 100;

const formatBRL = (valor) =>
	(Number(valor) || 0).toLocaleString("pt-br", {
		style: "currency",
		currency: "BRL",
	});

/**
 * Checkout do funil v3 (motor v2). Diferente do CheckoutDireto (que recebe um
 * planoId na URL), este é dirigido pela OFERTA da assinatura do usuário logado:
 * lê GET /plano-atual (v2) pra descobrir a oferta/fase/preço (o 9,90 do 1º mês)
 * e paga via POST /checkout-oferta. É pra onde o cadastro do funil redireciona
 * a conta pendente_ativacao (e o gate do back manda pra cá também).
 *
 * Tela ÚNICA: endereço, CPF e cartão/PIX são pedidos de uma vez no passo de
 * pagamento (CheckoutPagamento). O endereço/CPF da conta são persistidos no
 * submit, ANTES da cobrança — o back monta o cliente Asaas com usuario.*.
 */
const CheckoutOferta = () => {
	const history = useHistory();
	const isAppWebView = useIsAppWebView();

	const [loading, setLoading] = useState(true);
	const [planoAtual, setPlanoAtual] = useState(null);
	const [userData, setUserData] = useState(null);
	const [taxas, setTaxas] = useState([]);

	const [etapa, setEtapa] = useState("loading"); // loading, checkout, sucesso, erro, app_neutro
	const [checkoutError, setCheckoutError] = useState("");
	const [checkoutResult, setCheckoutResult] = useState(null);
	const [contratoLoading, setContratoLoading] = useState(false);
	const [pixCobrancaId, setPixCobrancaId] = useState(null);

	// CPF já usou o intro (9,90): se o usuário aceitar seguir pelo preço do
	// plano base, guardamos o valor (centavos, vindo do erro do back) pra
	// sobrepor o preço exibido e reenviar com a flag aceitarPrecoBase.
	const [precoBaseCentavos, setPrecoBaseCentavos] = useState(null);
	// Modal de aviso (CPF já usou a oferta): exige marcar o checkbox de aceite
	// ANTES de habilitar o "Continuar". modalPrecoBase guarda o preço base
	// pendente (null = fechado); aceiteBase é o checkbox que libera o botão.
	const [modalPrecoBase, setModalPrecoBase] = useState(null);
	const [aceiteBase, setAceiteBase] = useState(false);
	// Último payload submetido, pra reenviar idêntico + aceitarPrecoBase:true.
	const lastPayloadRef = useRef(null);

	useEffect(() => {
		// No app do profissional (WebView iOS), checkout é suprimido (IAP da
		// Apple): mostra a tela neutra direto, sem chamar a API de pagamento.
		if (isAppWebView) {
			setEtapa("app_neutro");
			setLoading(false);
			return;
		}
		carregarDados();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Dispara InitiateCheckout no Meta Pixel uma vez (valor = intro do funil).
	const initiateCheckoutFiredRef = useRef(false);
	useEffect(() => {
		if (etapa !== "checkout" || initiateCheckoutFiredRef.current) return;
		initiateCheckoutFiredRef.current = true;
		try {
			if (typeof window !== "undefined" && window.fbq) {
				window.fbq("track", "InitiateCheckout", {
					value: reais(planoAtual?.preco_fixo_centavos),
					currency: "BRL",
					content_name: planoAtual?.oferta?.nome || "Funil",
					content_type: "product",
				});
			}
		} catch {}
	}, [etapa, planoAtual]);

	// Poll PIX status até confirmar (webhook é o caminho rápido; polling é o piso).
	useEffect(() => {
		if (!pixCobrancaId) return;
		const interval = setInterval(async () => {
			try {
				const status =
					await planoContratualService.verificarStatusCobranca(
						pixCobrancaId,
					);
				if (
					status?.status === "CONFIRMED" ||
					status?.status === "RECEIVED"
				) {
					clearInterval(interval);
					setPixCobrancaId(null);
					setEtapa("sucesso");
				}
			} catch (e) {
				console.error(e);
			}
		}, 5000);
		return () => clearInterval(interval);
	}, [pixCobrancaId]);

	const carregarDados = async () => {
		setLoading(true);

		// O /plano-atual é lido SEPARADO e com erro EXPLÍCITO: um erro de rede
		// não pode virar "sem motor v2" (que redirecionaria pra /app/assinatura
		// → o /menu manda de volta pra cá → loop). Em erro, mostra retry.
		let planoAtualRes;
		try {
			planoAtualRes = await planoContratualService.getPlanoAtual();
		} catch (e) {
			console.error(e);
			setEtapa("erro");
			setLoading(false);
			return;
		}

		// Sem motor v2 (gate desligado / conta legada): não há funil pra
		// ativar — manda pra tela de assinatura padrão. Sem loop: o /menu não
		// acha assinatura v2 e não redireciona de volta.
		if (!planoAtualRes || planoAtualRes.motor !== "v2") {
			history.replace("/app/assinatura");
			return;
		}

		const estado = planoAtualRes.estado;
		// Já ativa: nada a pagar aqui.
		if (estado === "ativa" || estado === "trial") {
			history.replace("/app");
			return;
		}
		// Pendência financeira de conta já ativada antes: é regularização,
		// não ativação de funil — vai pra conta-bloqueada.
		if (estado === "inadimplente" || estado === "bloqueada") {
			history.replace("/app/conta-bloqueada");
			return;
		}

		// pendente_ativacao | dormente | cancelada: ativa/reativa por aqui.
		try {
			const [userRes, taxasRes] = await Promise.all([
				usuarioService.obterPropriosDados(),
				planoContratualService.getTaxasParcelamentoCartao(),
			]);

			setPlanoAtual(planoAtualRes);
			setUserData(userRes);
			setTaxas(taxasRes || []);

			// Tela única: endereço/CPF/cartão são coletados no passo de
			// pagamento; o endereço da conta é persistido no submit.
			setEtapa("checkout");
		} catch (e) {
			console.error(e);
			setEtapa("erro");
		}
		setLoading(false);
	};

	const fixedValue =
		precoBaseCentavos != null
			? reais(precoBaseCentavos)
			: reais(planoAtual?.preco_fixo_centavos);
	const qtdAlunos = planoAtual?.quantidadeAlunos || 0;
	const variableMonthlyValue =
		reais(planoAtual?.preco_variavel_centavos) * qtdAlunos;

	// Consulta o ViaCEP e devolve { logradouro, bairro } (ou null). Usado pra
	// completar o endereço da conta a partir do CEP informado no pagamento.
	const consultarCep = async (cep) => {
		const digits = (cep || "").replace(/\D/g, "");
		if (digits.length !== 8) return null;
		try {
			const response = await fetch(
				`https://viacep.com.br/ws/${digits}/json/`,
			);
			const data = await response.json();
			if (data?.erro) return null;
			return { logradouro: data.logradouro, bairro: data.bairro };
		} catch (e) {
			console.error(e);
			return null;
		}
	};

	// Antes de cobrar, persiste endereço + CPF na conta a partir dos dados do
	// titular (o back monta o cliente Asaas com usuario.*). Best-effort: se algo
	// falhar, segue pro pagamento — o holderInfo do cartão já vai na cobrança.
	const persistirDadosDaConta = async (payload) => {
		// Cartão manda holder; PIX manda `pagador` (CEP+número agora são
		// coletados nos dois métodos — cliente Asaas sem endereço trava a NF).
		const holder =
			payload?.creditCardHolderInfo || payload?.pagador || {};
		const cpf = (payload?.cpf || holder.cpfCnpj || "")
			.toString()
			.replace(/\D/g, "");
		const cep = (holder.postalCode || "").toString().replace(/\D/g, "");
		const numero = (holder.addressNumber || "").toString().trim();
		const complemento = (holder.addressComplement || "")
			.toString()
			.trim();

		const requests = [];
		if (cep && numero) {
			const via = await consultarCep(cep);
			requests.push(
				usuarioService.atualizarEndereco({
					cep,
					numero_endereco: numero,
					complemento_endereco: complemento,
					logradouro: via?.logradouro || userData?.logradouro || "",
					bairro: via?.bairro || userData?.bairro || "",
				}),
			);
		}
		if (cpf && !userData?.cpf) {
			requests.push(usuarioService.atualizarPropriosDados({ cpf }));
		}
		if (requests.length) {
			await Promise.all(requests);
		}
	};

	const handleCheckoutSubmit = async (payload, { aceitarPrecoBase } = {}) => {
		// Guarda o payload pra um eventual retry no preço base (resubmit idêntico
		// + flag) disparado pelo Modal abaixo.
		lastPayloadRef.current = payload;
		// FLAG STICKY: uma vez aceito o preço base (precoBaseCentavos != null),
		// todo resubmit reenvia a flag — inclusive o do botão do CheckoutPagamento,
		// que chama handleCheckoutSubmit sem o 2º arg. Assim um retry transitório
		// (cartão recusado/rede) não volta a tentar o intro (9,90) nem reabre o Modal.
		const enviarBase = aceitarPrecoBase || precoBaseCentavos != null;
		try {
			setContratoLoading(true);
			setCheckoutError("");

			// Persiste endereço/CPF na conta antes de cobrar (best-effort; o
			// cliente Asaas é montado com usuario.* no back).
			try {
				await persistirDadosDaConta(payload);
			} catch (e) {
				console.error("Falha ao salvar dados da conta:", e);
			}

			const resp = await planoContratualService.checkoutOferta(
				planoAtual.id_oferta,
				{
					...payload,
					...(enviarBase ? { aceitarPrecoBase: true } : {}),
				},
			);

			setCheckoutResult(resp);
			if (resp?.status === "PIX" && resp?.cobranca?.id) {
				setPixCobrancaId(resp.cobranca.id);
			}
			if (resp?.status === "PAID") {
				notification.success({
					message: "Pagamento confirmado",
					description: "Sua assinatura foi ativada com sucesso.",
				});
				setEtapa("sucesso");
			}
		} catch (error) {
			// CPF já usou a oferta de 1º mês: o back recusa (400) com o code e
			// devolve o preço do plano base. Em vez do erro genérico, oferece
			// seguir pelo preço base (resubmit idêntico + aceitarPrecoBase:true).
			// Atrás do guard isAppWebView: na WebView a tela é "app_neutro" e o
			// checkout nunca chega aqui — fallback de segurança mesmo assim.
			//
			// Só abre o Modal se: (a) o submit ainda NÃO carregava a base
			// (!enviarBase) — senão vira loop quando a oferta está mal configurada
			// (sem fase base) e o back re-lança o 400 mesmo com a flag; e (b) o
			// preço base veio válido (finito e > 0) — sem isso o Modal anunciaria
			// 'R$ 0,00'. Falhando qualquer uma, cai no tratamento genérico abaixo.
			const precoBase = error.response?.data?.precoBaseCentavos;
			if (
				!isAppWebView &&
				!enviarBase &&
				error.response?.data?.code === "OFERTA_INTRO_JA_USADA" &&
				Number.isFinite(precoBase) &&
				precoBase > 0
			) {
				// Abre o modal controlado de aviso. O botão "Continuar" só
				// habilita depois que o usuário marca o checkbox de aceite
				// (Modal renderizado abaixo, no return).
				setModalPrecoBase(precoBase);
				setAceiteBase(false);
				return;
			}

			const descricaoErro =
				error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.description ||
				error.response?.data?.error?.message ||
				error.message;
			setCheckoutError(
				descricaoErro ||
					"Não foi possível processar o pagamento. Verifique os dados e tente novamente.",
			);
		} finally {
			setContratoLoading(false);
		}
	};

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: 80 }}>
				<Spin size="large" />
			</div>
		);
	}

	if (etapa === "erro") {
		return (
			<Result
				status="warning"
				title="Não foi possível carregar sua assinatura"
				subTitle="Verifique sua conexão e tente novamente."
				extra={
					<Button type="primary" onClick={carregarDados}>
						Tentar novamente
					</Button>
				}
			/>
		);
	}

	if (etapa === "app_neutro") {
		return (
			<Result
				status="info"
				title="Conclua sua assinatura pelo navegador"
				subTitle="Para ativar sua conta, acesse o treino.io pelo navegador do computador ou do celular e finalize o pagamento. Depois é só voltar para o app."
				extra={
					<Button
						type="primary"
						onClick={() =>
							window.open("https://app.treino.io", "_blank")
						}
					>
						Abrir no navegador
					</Button>
				}
			/>
		);
	}

	if (etapa === "sucesso") {
		return (
			<Result
				status="success"
				title="Assinatura ativada com sucesso!"
				subTitle="Sua conta está liberada. Você já pode usar todas as funcionalidades da plataforma."
				extra={
					<Button type="primary" onClick={() => history.push("/app/inicio-rapido")}>
						Acessar a plataforma
					</Button>
				}
			/>
		);
	}

	return (
		<div>
			<div style={{ textAlign: "center", marginBottom: 32 }}>
				<img
					src={`${PUBLIC_PREFIX_PATH}/logo-comprido.png`}
					alt="treino.io"
					style={{ height: 40, marginBottom: 16 }}
				/>
				<Title level={3} style={{ margin: 0 }}>
					Ative sua conta
				</Title>
				<Text type="secondary">
					Conclua o pagamento para começar a usar a plataforma.
				</Text>
			</div>

			{etapa === "checkout" && (
				<CheckoutPagamento
					planName={planoAtual?.oferta?.nome || "Assinatura"}
					features={[]}
					telefoneInternacional
					fixedValue={fixedValue}
					variableMonthlyValue={variableMonthlyValue}
					studentsCount={qtdAlunos}
					totalValue={fixedValue + variableMonthlyValue}
					maxInstallments={1}
					taxasParcelamentoCartao={taxas}
					loading={contratoLoading}
					errorMessage={checkoutError}
					result={checkoutResult}
					cartaoSalvo={userData?.dados_cartao_plano || null}
					onSubmit={handleCheckoutSubmit}
					submitLabel="ATIVAR ASSINATURA"
					consentimentoFlipVersao={
						planoAtual?.oferta?.clausula_flip_versao || null
					}
					initialHolderEmail={userData?.email || ""}
					initialHolderCpfCnpj={userData?.cpf || ""}
					initialHolderPostalCode={userData?.cep || ""}
					initialHolderAddressNumber={userData?.numero_endereco || ""}
					initialHolderAddressComplement={
						userData?.complemento_endereco || ""
					}
					initialHolderPhone={
						userData?.telefone || userData?.telefone_celular || ""
					}
					initialHolderMobilePhone={userData?.telefone_celular || ""}
				/>
			)}

			<Modal
				visible={modalPrecoBase != null}
				title="Promoção já utilizada"
				okText={
					modalPrecoBase != null
						? `Continuar por ${formatBRL(reais(modalPrecoBase))}`
						: "Continuar"
				}
				cancelText="Cancelar"
				okButtonProps={{ disabled: !aceiteBase }}
				onCancel={() => {
					setModalPrecoBase(null);
					setAceiteBase(false);
				}}
				onOk={() => {
					const precoBase = modalPrecoBase;
					setModalPrecoBase(null);
					setAceiteBase(false);
					setPrecoBaseCentavos(precoBase);
					setCheckoutError("");
					handleCheckoutSubmit(lastPayloadRef.current, {
						aceitarPrecoBase: true,
					});
				}}
			>
				<p>
					Essa promoção de primeiro mês é válida uma vez por CPF. Você
					pode continuar pelo preço normal do plano base
					{modalPrecoBase != null
						? `, ${formatBRL(reais(modalPrecoBase))} por mês`
						: ""}
					.
				</p>
				<Checkbox
					checked={aceiteBase}
					onChange={(e) => setAceiteBase(e.target.checked)}
				>
					Entendo que a promoção de primeiro mês não se aplica a este
					CPF e aceito continuar pelo valor normal do plano base.
				</Checkbox>
			</Modal>
		</div>
	);
};

export default CheckoutOferta;
